/* istanbul ignore file */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Chevron } from "@app/components/shared/Chevron";
import {
  getSmtpSettings,
  updateSmtpSettings,
  testSmtpSettings,
  type SmtpSettings,
} from "@app/services/adminSettingsApi";

const SMTP_QUERY_KEY = ["admin", "smtp-settings"];

interface AdminEmailSettingsCardProps {
  isAdmin: boolean;
}

export function AdminEmailSettingsCard({
  isAdmin,
}: AdminEmailSettingsCardProps): JSX.Element | null {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [host, setHost] = useState("");
  const [port, setPort] = useState("587");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [useTls, setUseTls] = useState(true);
  const [testEmail, setTestEmail] = useState("");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: settings } = useQuery<SmtpSettings>({
    queryKey: SMTP_QUERY_KEY,
    queryFn: getSmtpSettings,
    enabled: isAdmin && open,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!settings) {
      return;
    }
    if (settings.host) {
      setHost(settings.host);
    }
    if (settings.port) {
      setPort(String(settings.port));
    }
    if (settings.username) {
      setUsername(settings.username);
    }
    if (settings.from_email) {
      setFromEmail(settings.from_email);
    }
    if (typeof settings.use_tls === "boolean") {
      setUseTls(settings.use_tls);
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: updateSmtpSettings,
    onMutate: () => {
      setError(null);
      setStatusMessage(null);
    },
    onSuccess: (result) => {
      setStatusMessage(result.message);
      setPassword("");
      queryClient.invalidateQueries({ queryKey: SMTP_QUERY_KEY });
    },
    onError: (err: unknown) => {
      setError(
        err instanceof Error ? err.message : "Failed to save SMTP settings.",
      );
    },
  });

  const testMutation = useMutation({
    mutationFn: testSmtpSettings,
    onMutate: () => {
      setError(null);
      setStatusMessage(null);
    },
    onSuccess: (result) => setStatusMessage(result.message),
    onError: (err: unknown) => {
      setError(
        err instanceof Error ? err.message : "Failed to send test email.",
      );
    },
  });

  if (!isAdmin) {
    return null;
  }

  const handleSave = () => {
    if (!host.trim()) {
      setError("SMTP host is required.");
      return;
    }
    if (!fromEmail.trim()) {
      setError("From email is required.");
      return;
    }

    saveMutation.mutate({
      host: host.trim() || null,
      port: port ? Number(port) : null,
      username: username.trim() || null,
      password: password.trim() || null,
      from_email: fromEmail.trim() || null,
      use_tls: useTls,
    });
  };

  const handleTest = () => {
    if (!testEmail.trim()) {
      setError("Enter a recipient email to send the test to.");
      return;
    }
    testMutation.mutate(testEmail.trim());
  };

  const showPasswordHint =
    settings?.password_set && !password
      ? "Password is set (leave blank to keep it)."
      : null;

  return (
    <Card
      className="overview-card integrations-card"
      title="Email (SMTP) Settings"
      subtitle="Configure the outgoing email server used for broadcasts."
      actions={
        <button
          type="button"
          className="ui-card__toggle"
          aria-expanded={open}
          aria-label={open ? "Collapse email settings" : "Expand email settings"}
          onClick={() => setOpen((prev) => !prev)}
        >
          <Chevron open={open} />
        </button>
      }
    >
      {!open ? null : (
        <>
          <div className="section-legend">
            <span className="section-legend__dot" />
            <div>
              Set the SMTP server, then send a test email to verify delivery
              end-to-end. Changes take effect immediately; the password is
              stored encrypted.
            </div>
          </div>

          <form
            className="integrations-form"
            onSubmit={(event) => {
              event.preventDefault();
              handleSave();
            }}
          >
            <label>
              SMTP Host
              <input
                type="text"
                value={host}
                onChange={(event) => setHost(event.target.value)}
                placeholder="smtp.gmail.com"
              />
            </label>

            <label>
              Port
              <input
                type="number"
                value={port}
                onChange={(event) => setPort(event.target.value)}
                placeholder="587"
              />
            </label>

            <label>
              Username
              <input
                type="text"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="you@example.com"
              />
            </label>

            <label>
              Password (app password)
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder={showPasswordHint ?? "Enter password"}
              />
            </label>

            <label>
              From Email
              <input
                type="text"
                value={fromEmail}
                onChange={(event) => setFromEmail(event.target.value)}
                placeholder="you@example.com"
              />
            </label>

            <div className="integrations-checkbox">
              <input
                type="checkbox"
                checked={useTls}
                onChange={(event) => setUseTls(event.target.checked)}
              />
              Use STARTTLS
            </div>

            {error ? <p className="integrations-error">{error}</p> : null}
            {statusMessage ? (
              <p className="integrations-summary" style={{ marginTop: "8px" }}>
                {statusMessage}
              </p>
            ) : null}

            <div className="integrations-actions">
              <Button
                type="submit"
                variant="primary"
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? "Saving..." : "Save email settings"}
              </Button>
            </div>
          </form>

          <div
            style={{
              marginTop: "16px",
              borderTop: "1px solid var(--color-border)",
              paddingTop: "16px",
            }}
          >
            <label>
              Send test email to
              <input
                type="email"
                value={testEmail}
                onChange={(event) => setTestEmail(event.target.value)}
                placeholder="recipient@example.com"
              />
            </label>
            <div className="integrations-actions">
              <Button
                type="button"
                variant="secondary"
                disabled={testMutation.isPending}
                onClick={handleTest}
              >
                {testMutation.isPending ? "Sending test..." : "Send test email"}
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
